import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';

import { getLogoutAction } from '@epi-actions/login';
import { routerPaths } from '@epi-constants/routerPaths';
import { removeLocale } from '@epi-helpers/UrlHelpers';
import { beneficiariesImportActions } from '@epi-store/beneficiariesImport/beneficiariesImport.slice';
import { selectLeaveConfirmationRequested } from '@epi-store/beneficiariesImport/beneficiariesImport.selectors';

type PendingNavigation = {
  pathname: string;
  search: string;
  hash: string;
};

const isLoginPath = (pathname: string) =>
  removeLocale(pathname) === routerPaths.login;

export const useLeaveImportEmployeesConfirmation = (
  when = true,
  onLeave?: () => void
) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const leaveConfirmationRequested = useSelector(
    selectLeaveConfirmationRequested
  );
  const onLeaveRef = useRef(onLeave);
  const [isOpen, setIsOpen] = useState(false);
  const pendingNavigation = useRef<PendingNavigation | null>(null);
  const pendingLogoutRef = useRef(false);
  const unblockRef = useRef<(() => void) | null>(null);
  const allowNextNavigationRef = useRef(false);

  onLeaveRef.current = onLeave;

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  useEffect(() => {
    if (!leaveConfirmationRequested) return;

    dispatch(beneficiariesImportActions.clearLeaveConfirmationRequest());

    if (!when) {
      dispatch(getLogoutAction());
      return;
    }

    pendingLogoutRef.current = true;
    setIsOpen(true);
  }, [dispatch, leaveConfirmationRequested, when]);

  useEffect(() => {
    if (!when) return;

    unblockRef.current = history.block((location: Location) => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return undefined;
      }

      if (isLoginPath(location.pathname)) {
        onLeaveRef.current?.();
        return undefined;
      }

      pendingNavigation.current = {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash
      };
      setIsOpen(true);
      return false;
    });

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [history, when]);

  const cancelLeave = useCallback(() => {
    pendingNavigation.current = null;
    pendingLogoutRef.current = false;
    setIsOpen(false);
  }, []);

  const confirmLeave = useCallback(() => {
    const navigation = pendingNavigation.current;
    const isLogout = pendingLogoutRef.current;
    pendingNavigation.current = null;
    pendingLogoutRef.current = false;
    setIsOpen(false);
    onLeave?.();

    if (isLogout) {
      unblockRef.current?.();
      dispatch(getLogoutAction());
      return;
    }

    if (!navigation) return;

    unblockRef.current?.();
    history.push(
      `${navigation.pathname}${navigation.search}${navigation.hash}`
    );
  }, [dispatch, history, onLeave]);

  return { isOpen, confirmLeave, cancelLeave, allowNextNavigation };
};
